import { io } from "socket.io-client";

const CHAT_SERVER_URL =
  process.env.REACT_APP_CHAT_SERVER_URL || "http://localhost:5000";

class ChatService {
  constructor() {
    this.socket = null;
    this._callbacks = new Map();
    this._activeGroups = new Set();
  }

  // ─── Core Management ──────────────────────────────────────────

  connect(userId, role, token) {
    if (this.socket) return;
    this.userId = userId;
    this.role = role;

    this.socket = io(CHAT_SERVER_URL, {
      auth: { token },
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      transports: ["polling", "websocket"],
    });

    this._setupSocketListeners(userId, role);
  }

  _setupSocketListeners(userId, role) {
    this.socket.on("connect", () => {
      this.socket.emit("join", { userId, role });
      this._activeGroups.forEach((gid) => this.joinGroup(gid));
      this._emit("connectionChange", "connected");
    });

    this.socket.on("disconnect", () =>
      this._emit("connectionChange", "disconnected"),
    );
    this.socket.io.on("reconnect", () =>
      this._emit("connectionChange", "connected"),
    );

    // Generic Event Forwarding
    const events = [
      "message",
      "groupMessage",
      "systemAlert",
      "messagesRead",
      "userOnline",
      "userOffline",
      "messageReactionUpdate",
      "messageStarred",
      "searchResult",
      "pollUpdated",
      "messageFlagged",
      "assignmentGraded",
      "messageEdited",
      "messageDeleted",
      "groupCreated",
      "groupUpdated",
      "memberAdded",
      "memberRemoved",
      "groupDeleted",
      "addedToGroup",
      "removedFromGroup",
      "messageInfo",
      "groupMuted",
      "messagePinned",
      "monitorUpdate",
      "messagesDelivered"
    ];

    events.forEach((event) => {
      this.socket.on(event, (data) => {
        if (event === "message" && data.groupId) {
          this._emit("groupMessage", data);
        } else {
          this._emit(event, data);
        }
      });
    });

    this.socket.on("userTyping", (data) =>
      this._emit("typing", { ...data, isTyping: true }),
    );
    this.socket.on("userStopTyping", (data) =>
      this._emit("typing", { ...data, isTyping: false }),
    );
    this.socket.on("messageError", (err) => {
      console.error("CHAT SERVER ERROR:", err);
      this._emit("messageError", err);
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this._callbacks.clear();
      this._activeGroups.clear();
    }
  }

  // ─── Subscriptions ───────────────────────────────────────────

  _subscribe(event, callback) {
    if (!this._callbacks.has(event)) this._callbacks.set(event, []);
    this._callbacks.get(event).push(callback);
    return () => {
      const list = this._callbacks.get(event);
      if (list)
        this._callbacks.set(
          event,
          list.filter((cb) => cb !== callback),
        );
    };
  }

  _emit(event, data) {
    const list = this._callbacks.get(event);
    if (list) list.forEach((cb) => cb(data));
  }

  // Public Subscription Aliases (to maintain backward compatibility)
  onMessage(cb) {
    return this._subscribe("message", cb);
  }
  onGroupMessage(cb) {
    return this._subscribe("groupMessage", cb);
  }
  onSystemAlert(cb) {
    return this._subscribe("systemAlert", cb);
  }
  onTyping(cb) {
    return this._subscribe("typing", cb);
  }
  onMessagesRead(cb) {
    return this._subscribe("messagesRead", cb);
  }
  onConnectionChange(cb) {
    return this._subscribe("connectionChange", cb);
  }
  onOnlineStatus(cb) {
    const sub1 = this._subscribe("userOnline", (d) =>
      cb({ userId: d.userId, online: true }),
    );
    const sub2 = this._subscribe("userOffline", (d) =>
      cb({ userId: d.userId, online: false }),
    );
    const sub3 = this._subscribe("onlineUsersList", (d) =>
      cb({ userIds: d.userIds, initial: true }),
    );
    return () => {
      sub1();
      sub2();
      sub3();
    };
  }
  onReactionUpdate(cb) {
    return this._subscribe("messageReactionUpdate", cb);
  }
  onMessageStarred(cb) {
    return this._subscribe("messageStarred", cb);
  }
  onSearchResult(cb) {
    return this._subscribe("searchResult", cb);
  }
  onPollUpdated(cb) {
    return this._subscribe("pollUpdated", cb);
  }
  onMessageFlagged(cb) {
    return this._subscribe("messageFlagged", cb);
  }
  onAssignmentGraded(cb) {
    return this._subscribe("assignmentGraded", cb);
  }
  onMessageEdited(cb) {
    return this._subscribe("messageEdited", cb);
  }
  onMessageDeleted(cb) {
    return this._subscribe("messageDeleted", cb);
  }
  onMessageError(cb) {
    return this._subscribe("messageError", cb);
  }
  onGroupCreated(cb) {
    return this._subscribe("groupCreated", cb);
  }
  onGroupUpdated(cb) {
    return this._subscribe("groupUpdated", cb);
  }
  onMemberAdded(cb) {
    return this._subscribe("memberAdded", cb);
  }
  onMemberRemoved(cb) {
    return this._subscribe("memberRemoved", cb);
  }
  onGroupDeleted(cb) {
    return this._subscribe("groupDeleted", cb);
  }
  onAddedToGroup(cb) {
    return this._subscribe("addedToGroup", cb);
  }
  onRemovedFromGroup(cb) {
    return this._subscribe("removedFromGroup", cb);
  }
  onMessageInfo(cb) {
    return this._subscribe("messageInfo", cb);
  }
  onGroupMuted(cb) {
    return this._subscribe("groupMuted", cb);
  }
  onMessagePinned(cb) {
    return this._subscribe("messagePinned", cb);
  }
  onMonitorUpdate(cb) {
    return this._subscribe("monitorUpdate", cb);
  }
  onMessagesDelivered(cb) {
    return this._subscribe("messagesDelivered", cb);
  }
  onConnectionStateChange(cb) {
    return this._subscribe("connectionChange", cb);
  }

  // ─── Actions ──────────────────────────────────────────────────

  sendMessage(data) {
    this.socket?.emit("sendMessage", data);
  }
  sendTyping({ receiverId, groupId, isTyping }) {
    const event = isTyping ? "typing" : "stopTyping";
    this.socket?.emit(event, groupId ? { groupId } : { receiverId });
  }
  markRead(messageIds, readerId) {
    this.socket?.emit("markRead", { messageIds, readerId });
  }

  joinGroup(groupId) {
    this._activeGroups.add(groupId);
    this.socket?.emit("joinGroup", { groupId });
  }
  leaveGroup(groupId) {
    this._activeGroups.delete(groupId);
    this.socket?.emit("leaveGroup", { groupId });
  }

  createGroup(data) {
    this.socket?.emit("createGroup", data);
  }
  updateGroup(id, upd) {
    this.socket?.emit("updateGroup", { groupId: id, ...upd });
  }
  deleteGroup(id) {
    this.socket?.emit("deleteGroup", { groupId: id });
  }

  toggleReaction(id, em, userId) {
    this.socket?.emit("reactToMessage", {
      messageId: id,
      emoji: em,
      userId: userId,
    });
  }
  reportMessage(id, res) {
    this.socket?.emit("reportMessage", { messageId: id, reason: res });
  }
  gradeAssignment(id, g, c) {
    this.socket?.emit("gradeAssignment", {
      messageId: id,
      grade: g,
      comment: c,
    });
  }
  deleteMessage(id, m = "all") {
    this.socket?.emit("deleteMessage", { messageId: id, mode: m });
  }
  editMessage(id, content) {
    this.socket?.emit("editMessage", { messageId: id, newContent: content });
  }
  getMessageInfo(id) {
    this.socket?.emit("getMessageInfo", { messageId: id });
  }
  pinMessage(id, status) {
    this.socket?.emit("pinMessage", { messageId: id, status });
  }
  toggleGroupMute(id, muted) {
    this.socket?.emit("toggleGroupMute", { groupId: id, isMuted: muted });
  }
  forwardMessage(id, target, isGroup) {
    this.socket?.emit("forwardMessage", {
      messageId: id,
      targetId: target,
      isGroup,
    });
  }
  toggleStar(id) {
    this.socket?.emit("toggleStar", { messageId: id });
  }
  searchMessages(q, t, g) {
    this.socket?.emit("searchMessages", { query: q, targetId: t, isGroup: g });
  }
  createPoll(t, g, q, opt, exp) {
    this.socket?.emit("createPoll", {
      targetId: t,
      isGroup: g,
      question: q,
      options: opt,
      expiresAt: exp,
    });
  }
  votePoll(mId, opt) {
    this.socket?.emit("votePoll", { messageId: mId, optionIndex: opt });
  }
  startMonitoring() {
    this.socket?.emit("startMonitoring");
  }
  stopMonitoring() {
    this.socket?.emit("stopMonitoring");
  }
  getOnlineUsers() {
    this.socket?.emit("getOnlineUsers");
  }
}

export { ChatService };
